import { create } from "zustand";
import { persist } from "zustand/middleware";

// Types matching backend
export type SupportedAsset = "BTC/USD" | "ETH/USD" | "SOL/USD";
export type OrderSide = "long" | "short";
export type OrderType = "market" | "limit";
export type OrderStatus =
  | "pending"
  | "open"
  | "filled"
  | "partially_filled"
  | "cancelled"
  | "expired";
export type PositionStatus = "open" | "closed";
export type TradeSide = "buy" | "sell";

export interface PaperTradingAccount {
  id: string;
  walletAddress: string;
  balance: number;
  marginUsed: number;
  availableBalance: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface Position {
  id: string;
  accountId: string;
  asset: SupportedAsset;
  side: OrderSide;
  size: number;
  entryPrice: number;
  leverage: number;
  margin: number;
  liquidationPrice: number;
  realizedPnl: number;
  unrealizedPnl: number;
  takeProfitPrice: number | null;
  stopLossPrice: number | null;
  status: PositionStatus;
  createdAt: Date;
}

export interface Order {
  id: string;
  accountId: string;
  asset: SupportedAsset;
  type: OrderType;
  side: OrderSide;
  size: number;
  price: number | null;
  filledSize: number;
  avgFillPrice: number | null;
  status: OrderStatus;
  leverage: number;
  fee: number;
  createdAt: Date;
  filledAt: Date | null;
}

export interface Trade {
  id: string;
  orderId: string;
  accountId: string;
  positionId: string | null;
  asset: SupportedAsset;
  side: TradeSide;
  size: number;
  price: number;
  fee: number;
  realizedPnl: number;
  timestamp: Date;
}

export interface OrderFormData {
  side: OrderSide;
  type: OrderType;
  size: string;
  price: string;
  leverage: number;
  reduceOnly: boolean;
}

interface PaperTradingState {
  // Account
  account: PaperTradingAccount | null;
  isLoadingAccount: boolean;

  // Positions
  positions: Position[];
  isLoadingPositions: boolean;

  // Orders
  orders: Order[];
  isLoadingOrders: boolean;

  // Trades
  trades: Trade[];
  isLoadingTrades: boolean;

  // Current prices (from market data)
  currentPrices: Record<SupportedAsset, number>;

  // Order form state
  orderForm: OrderFormData;

  // UI state
  activeTab: "positions" | "orders" | "trades";
  selectedPositionId: string | null;
  isSubmittingOrder: boolean;
  error: string | null;

  // Actions - Account
  setAccount: (account: PaperTradingAccount | null) => void;
  setLoadingAccount: (loading: boolean) => void;
  updateBalance: (balance: number, marginUsed: number) => void;

  // Actions - Positions
  setPositions: (positions: Position[]) => void;
  setLoadingPositions: (loading: boolean) => void;
  updatePosition: (positionId: string, updates: Partial<Position>) => void;
  removePosition: (positionId: string) => void;
  addPosition: (position: Position) => void;

  // Actions - Orders
  setOrders: (orders: Order[]) => void;
  setLoadingOrders: (loading: boolean) => void;
  updateOrder: (orderId: string, updates: Partial<Order>) => void;
  removeOrder: (orderId: string) => void;
  addOrder: (order: Order) => void;

  // Actions - Trades
  setTrades: (trades: Trade[]) => void;
  setLoadingTrades: (loading: boolean) => void;
  addTrade: (trade: Trade) => void;

  // Actions - Prices
  updatePrice: (asset: SupportedAsset, price: number) => void;
  updatePrices: (prices: Record<SupportedAsset, number>) => void;

  // Actions - Order Form
  setOrderForm: (form: Partial<OrderFormData>) => void;
  resetOrderForm: () => void;

  // Actions - UI
  setActiveTab: (tab: "positions" | "orders" | "trades") => void;
  selectPosition: (positionId: string | null) => void;
  setSubmittingOrder: (submitting: boolean) => void;
  setError: (error: string | null) => void;

  // Actions - Reset
  reset: () => void;
}

const DEFAULT_ORDER_FORM: OrderFormData = {
  side: "long",
  type: "market",
  size: "",
  price: "",
  leverage: 5,
  reduceOnly: false,
};

const DEFAULT_PRICES: Record<SupportedAsset, number> = {
  "BTC/USD": 0,
  "ETH/USD": 0,
  "SOL/USD": 0,
};

export const usePaperTradingStore = create<PaperTradingState>()(
  persist(
    (set) => ({
      // Initial state
      account: null,
      isLoadingAccount: false,
      positions: [],
      isLoadingPositions: false,
      orders: [],
      isLoadingOrders: false,
      trades: [],
      isLoadingTrades: false,
      currentPrices: DEFAULT_PRICES,
      orderForm: DEFAULT_ORDER_FORM,
      activeTab: "positions",
      selectedPositionId: null,
      isSubmittingOrder: false,
      error: null,

      // Account actions
      setAccount: (account) => set({ account }),
      setLoadingAccount: (loading) => set({ isLoadingAccount: loading }),
      updateBalance: (balance, marginUsed) =>
        set((state) => ({
          account: state.account
            ? {
                ...state.account,
                balance,
                marginUsed,
                availableBalance: balance - marginUsed,
              }
            : null,
        })),

      // Position actions
      setPositions: (positions) => set({ positions }),
      setLoadingPositions: (loading) => set({ isLoadingPositions: loading }),
      updatePosition: (positionId, updates) =>
        set((state) => ({
          positions: state.positions.map((p) =>
            p.id === positionId ? { ...p, ...updates } : p
          ),
        })),
      removePosition: (positionId) =>
        set((state) => ({
          positions: state.positions.filter((p) => p.id !== positionId),
          selectedPositionId:
            state.selectedPositionId === positionId ? null : state.selectedPositionId,
        })),
      addPosition: (position) =>
        set((state) => ({
          positions: [position, ...state.positions.filter((p) => p.id !== position.id)],
        })),

      // Order actions
      setOrders: (orders) => set({ orders }),
      setLoadingOrders: (loading) => set({ isLoadingOrders: loading }),
      updateOrder: (orderId, updates) =>
        set((state) => ({
          orders: state.orders.map((o) => (o.id === orderId ? { ...o, ...updates } : o)),
        })),
      removeOrder: (orderId) =>
        set((state) => ({
          orders: state.orders.filter((o) => o.id !== orderId),
        })),
      addOrder: (order) =>
        set((state) => ({
          orders: [order, ...state.orders.filter((o) => o.id !== order.id)],
        })),

      // Trade actions
      setTrades: (trades) => set({ trades }),
      setLoadingTrades: (loading) => set({ isLoadingTrades: loading }),
      addTrade: (trade) =>
        set((state) => ({
          trades: [trade, ...state.trades],
        })),

      // Price actions
      updatePrice: (asset, price) =>
        set((state) => ({
          currentPrices: { ...state.currentPrices, [asset]: price },
        })),
      updatePrices: (prices) =>
        set((state) => ({
          currentPrices: { ...state.currentPrices, ...prices },
        })),

      // Order form actions
      setOrderForm: (form) =>
        set((state) => ({
          orderForm: { ...state.orderForm, ...form },
        })),
      resetOrderForm: () => set({ orderForm: DEFAULT_ORDER_FORM }),

      // UI actions
      setActiveTab: (tab) => set({ activeTab: tab }),
      selectPosition: (positionId) => set({ selectedPositionId: positionId }),
      setSubmittingOrder: (submitting) => set({ isSubmittingOrder: submitting }),
      setError: (error) => set({ error }),

      // Reset
      reset: () =>
        set({
          account: null,
          positions: [],
          orders: [],
          trades: [],
          orderForm: DEFAULT_ORDER_FORM,
          activeTab: "positions",
          selectedPositionId: null,
          isSubmittingOrder: false,
          error: null,
        }),
    }),
    {
      name: "paper-trading-store",
      partialize: (state) => ({
        // Only persist order form preferences
        orderForm: {
          leverage: state.orderForm.leverage,
          type: state.orderForm.type,
        },
        activeTab: state.activeTab,
      }),
    }
  )
);

// Selectors
export const selectOpenPositions = (state: PaperTradingState) =>
  state.positions.filter((p) => p.status === "open");

export const selectOpenOrders = (state: PaperTradingState) =>
  state.orders.filter((o) => ["open", "pending", "partially_filled"].includes(o.status));

export const selectTotalUnrealizedPnl = (state: PaperTradingState) =>
  state.positions
    .filter((p) => p.status === "open")
    .reduce((sum, p) => sum + (p.unrealizedPnl || 0), 0);

export const selectTotalMarginUsed = (state: PaperTradingState) =>
  state.positions
    .filter((p) => p.status === "open")
    .reduce((sum, p) => sum + p.margin, 0);

export const selectPositionsAtRisk = (state: PaperTradingState) => {
  return state.positions.filter((p) => {
    if (p.status !== "open") return false;
    const currentPrice = state.currentPrices[p.asset];
    if (!currentPrice) return false;

    const distancePercent =
      p.side === "long"
        ? ((currentPrice - p.liquidationPrice) / currentPrice) * 100
        : ((p.liquidationPrice - currentPrice) / currentPrice) * 100;

    return distancePercent < 10;
  });
};
